<template>
  <div>
    <data-table-wrapper
      :filter-config="filterConfig"
      :default-sort="{ by: 'billableDuration.start.timestamp', desc: false }"
      :default-items-per-page="50"
      :enable-search="true"
    >
      <template
        #default="{
          tableProps,
          tableEvents,
          filters,
          updateFilter,
          searchValue,
          updateSearch,
        }"
      >
        <v-data-table
          v-bind="tableProps"
          v-on="tableEvents"
          :headers="computedHeaders"
          :items="filteredTimeSheets"
          item-key="meta.id"
          :search="searchValue"
          groupBy="groupBy"
          class="elevation-2"
          max-height="800px auto"
          @click:row="routeDetails"
          :footer-props="footerProps"
          no-data-text="Keine Stundenmeldungen vorhanden"
          no-results-text="Keine Stundenmeldungen gefunden"
        >
          <template v-slot:top>
            <v-card flat>
              <v-card-subtitle>
                <v-row>
                  <v-col v-if="showAllTimeSheets" cols="12" sm="3" md="3">
                    <v-text-field
                      :value="searchValue"
                      @input="updateSearch"
                      outlined
                      append-icon="mdi-magnify"
                      label="Suchen"
                      :disabled="filters.personFilter === 'own'"
                      hide-details="auto"
                      clearable
                      :dense="$vuetify.breakpoint.smAndDown"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    v-if="showAllTimeSheets"
                    :class="$vuetify.breakpoint.smAndUp ? 'ml-2' : ''"
                  >
                    <span class="mr-4">Filter nach Typ</span>
                    <v-chip-group
                      :value="filters.personFilter"
                      @change="updateFilter('personFilter', $event)"
                      mandatory
                      active-class="primary--text"
                    >
                      <v-chip
                        label
                        value="all"
                        class="transparent-background font-weight-medium mr-2"
                        >Alle
                      </v-chip>
                      <v-chip
                        label
                        value="own"
                        class="transparent-background font-weight-medium"
                        >Eigene</v-chip
                      >
                    </v-chip-group>
                  </v-col>
                  <v-col>
                    <span class="mr-4">Filter nach Quelle</span>
                    <v-chip-group
                      :value="filters.sourceFilter"
                      @change="updateFilter('sourceFilter', $event)"
                      mandatory
                      active-class="primary--text"
                    >
                      <v-chip
                        label
                        value="all"
                        class="transparent-background font-weight-medium mr-2"
                        >Alle</v-chip
                      >
                      <v-chip
                        label
                        value="manual"
                        class="transparent-background font-weight-medium mr-2"
                        >Manuell</v-chip
                      >
                      <v-chip
                        label
                        value="dutyReport"
                        class="transparent-background font-weight-medium mr-2"
                        >Dienstbericht</v-chip
                      >
                    </v-chip-group>
                  </v-col>
                  <v-col>
                    <span class="mr-4">Filter nach Status</span>
                    <v-chip-group
                      :value="filters.stateFilter"
                      @change="updateFilter('stateFilter', $event)"
                      mandatory
                      active-class="primary--text"
                    >
                      <v-chip
                        label
                        value="all"
                        class="transparent-background font-weight-medium mr-2"
                        >Alle</v-chip
                      >
                      <v-chip
                        label
                        value="toBeReviewed"
                        class="transparent-background font-weight-medium mr-2"
                        >Zu prüfen</v-chip
                      >
                      <v-chip
                        label
                        value="missingPayrollType"
                        class="transparent-background font-weight-medium mr-2"
                        >Fehlerhaft</v-chip
                      >
                    </v-chip-group>
                  </v-col>
                  <v-col>
                    <span class="mr-4">Filter nach Abrechnungsart</span>
                    <v-select
                      :value="payrollTypeSelectValue"
                      @input="
                        (value) => handlePayrollTypeChange(value, updateFilter)
                      "
                      :items="payrollTypesList"
                      item-text="title"
                      item-value="meta.id"
                      multiple
                      outlined
                      hide-details="auto"
                      dense
                      small-chips
                    >
                      <template v-slot:prepend-item>
                        <v-list-item
                          ripple
                          @mousedown.prevent
                          @click="selectAllPayrollTypes(filters, updateFilter)"
                        >
                          <v-list-item-action>
                            <v-icon
                              :color="
                                payrollTypeSelectValue.length > 0
                                  ? 'primary'
                                  : ''
                              "
                            >
                              {{
                                getPayrollTypeSelectorIcon(
                                  filters.payrollTypeFilter,
                                )
                              }}
                            </v-icon>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>
                              Alle auswählen
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                      </template>
                      <template v-slot:selection="{ item, index }">
                        <v-chip v-if="index === 0" small label>
                          <span>{{ item.title }}</span>
                        </v-chip>
                        <span
                          v-if="index === 1"
                          small
                          label
                          class="grey--text text-caption"
                        >
                          (+{{ payrollTypeSelectValue.length - 1 }}
                          weitere)
                        </span>
                      </template></v-select
                    >
                  </v-col>
                  <v-col
                    ><table-column-selector-menu
                      v-if="showAllTimeSheets"
                      v-model="headers"
                      :headers="allColumnHeaders"
                    >
                    </table-column-selector-menu
                  ></v-col>

                  <v-col>
                    <span class="mr-4">Gruppen</span>
                    <v-btn
                      small
                      depressed
                      class="mr-2"
                      @click="expandAllGroups"
                      :disabled="!hasClosedGroups"
                    >
                      <v-icon small left>mdi-chevron-down</v-icon>
                      Alle ausklappen
                    </v-btn>
                    <v-btn
                      small
                      depressed
                      @click="collapseAllGroups"
                      :disabled="!hasOpenGroups"
                    >
                      <v-icon small left>mdi-chevron-up</v-icon>
                      Alle einklappen
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-subtitle>
            </v-card>
            <v-divider></v-divider>
          </template>

          <template v-slot:group.header="{ items, isOpen, toggle }">
            <th :colspan="headers.length">
              <div class="my-1">
                <v-btn
                  @click="
                    () => {
                      toggle();
                      updateGroupState();
                    }
                  "
                  small
                  icon
                  :ref="items[0].groupBy"
                  :data-open="isOpen"
                  class="mr-1"
                >
                  <v-icon>
                    {{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
                  </v-icon>
                </v-btn>
                {{ stripPrefix(items[0].groupBy) }}
                <span class="font-weight-regular grey--text"
                  >({{ getCountOfTimeSheetsInMonth(items[0].groupBy) }})</span
                >
              </div>

              <v-divider></v-divider>
              <div class="my-1">
                <small class="caption font-weight-regular"
                  >{{ items.length }} angezeigte Einträge in Monat mit einer
                  Summe von {{ getHoursSum(items) }} abrechenbaren Stunden und
                  einem berechneten Aufwand in Höhe von
                  {{ getCompensationSum(items) }} EUR</small
                >
              </div>
            </th>
          </template>
          <template v-slot:[`item.type`]="{ item }">
            <time-sheet-type-chip :type="item.type" small>
            </time-sheet-type-chip>
          </template>
          <template v-slot:[`item.payrollType.group.title`]="{ item }">
            <v-chip v-if="item.payrollType.group?.title" small outlined label>{{
              item.payrollType.group.title
            }}</v-chip>
          </template>
          <template v-slot:[`item.additionalData.assignedPosition`]="{ item }">
            <span
              v-if="
                item.additionalData?.assignedPosition?.unit?.title &&
                item.additionalData?.assignedPosition?.position?.shortTitle
              "
            >
              {{ item.additionalData.assignedPosition.unit.title }} –
              {{ item.additionalData.assignedPosition.position.shortTitle }}
            </span>
          </template>
          <template v-slot:[`item.payrollType`]="{ item }">
            <v-chip v-if="item.payrollType.title" small outlined label>{{
              item.payrollType.title
            }}</v-chip>
          </template>
          <template v-slot:[`item.billableDuration.start.timestamp`]="{ item }">
            {{ item.billableDuration.start.formattedTimestamp }}
          </template>
          <template v-slot:[`item.billableDuration.end.timestamp`]="{ item }">
            {{ item.billableDuration.end.formattedTimestamp }}
          </template>
          <template #[`item.status`]="{ item }">
            <time-sheet-status-chip :status="item.status" small>
            </time-sheet-status-chip>
          </template>
          <template #[`item.totalSalary.amount`]="{ item }">
            <pre
              >{{ item.totalSalary.amount }} {{
                item.totalSalary.currency
              }}</pre
            >
          </template>
          <template #[`item.totalSalary.baseVariables.HOURS`]="{ item }">
            {{ item.totalSalary.baseVariables.HOURS }}
          </template>
          <template #[`item.actions`]="{ item }"
            ><v-btn
              v-if="showActionBtn"
              depressed
              @click="openTimeSheet(item, 'newTab')"
              small
              ><v-icon left small>mdi-open-in-app</v-icon>Öffnen</v-btn
            ><v-menu v-else offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon text v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list nav>
                <v-list-item link @click="routeDetails(item)">
                  <v-list-item-icon>
                    <v-icon dense>mdi-open-in-app</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Öffnen</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item link @click="openTimeSheet(item, 'newTab')">
                  <v-list-item-icon>
                    <v-icon dense>mdi-open-in-new</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Öffnen (in neuem Tab)</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </template>
    </data-table-wrapper>
  </div>
</template>

<script>
import { auth } from "@/firebase";
import TableColumnSelectorMenu from "@/components/_system/helpers/TableColumnSelectorMenu.vue";
import TimeSheetStatusChip from "@/components/accounting/time-sheets/TimeSheetStatusChip.vue";
import TimeSheetTypeChip from "@/components/accounting/time-sheets/TimeSheetTypeChip.vue";
import DataTableWrapper from "@/components/_system/DataTableWrapper.vue";
import { ACCOUNTING_TIME_SHEETS_read_all } from "@/data/permission-types.js";
import { ACCOUNTING } from "@/store/modules.js";
import { GET_PAYROLL_TYPES } from "@/store/action-types.js";

export default {
  name: "time-sheets-data-table",
  props: {
    timeSheets: {
      type: Array,
      required: true,
    },
    showActionBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    TableColumnSelectorMenu,
    TimeSheetStatusChip,
    TimeSheetTypeChip,
    "data-table-wrapper": DataTableWrapper,
  },
  data() {
    return {
      currentUserUid: auth.currentUser.uid,
      // Simple state tracking for group expand/collapse - no complex reactivity
      allGroupsExpanded: true,
      hasClosedGroups: false,
      hasOpenGroups: true,
      filterConfig: {
        personFilter: {
          default: "own",
          urlKey: "personFilter",
        },
        sourceFilter: {
          default: "manual",
          urlKey: "sourceFilter",
        },
        stateFilter: {
          default: "active",
          urlKey: "stateFilter",
        },
        payrollTypeFilter: {
          default: "all",
          urlKey: "payrollTypeFilter",
        },
      },
      footerProps: {
        "items-per-page-options": [25, 50, 100, 150, 200, -1],
      },

      allColumnHeaders: [
        {
          text: "Vorname",
          sortKey: 1,
          default: true,
          value: "user.firstName",
        },
        {
          text: "Nachname",
          sortKey: 2,
          default: true,
          value: "user.lastName",
          divider: true,
        },
        {
          text: "Tätigkeit",
          value: "title",
          default: true,
          sortKey: 3,
          divider: true,
        },
        {
          text: "Typ",
          sortKey: 4,
          default: true,
          value: "type",
          filter: (value) => {
            const sourceFilter = this.$route.query.sourceFilter || "manual";
            if (sourceFilter === "all") return true;
            if (sourceFilter === "dutyReport") {
              return value === "dutyReport";
            }
            if (sourceFilter === "manual") {
              return value !== "dutyReport";
            }
          },
          divider: true,
        },
        {
          text: "Startzeitpunkt",
          sortKey: 5,
          default: true,
          value: "billableDuration.start.timestamp",
          divider: true,
        },
        {
          text: "Endzeitpunkt",
          sortKey: 6,
          default: true,
          value: "billableDuration.end.timestamp",
          divider: true,
        },
        {
          text: "Ort",
          sortKey: 7,
          default: false,
          value: "additionalData.location.name",
          divider: true,
        },
        {
          text: "Einsatzmittel",
          sortKey: 8,
          default: false,
          value: "additionalData.assignedPosition.unit.title",
          divider: true,
        },
        {
          text: "Funktion",
          sortKey: 9,
          default: false,
          value: "additionalData.assignedPosition.position.shortTitle",
          divider: true,
        },
        {
          text: "Einsatzmittel/Funktion",
          sortKey: 10,
          default: false,
          value: "additionalData.assignedPosition",
          divider: true,
        },
        {
          text: "Status",
          sortKey: 11,
          default: true,
          value: "status",
          filter: (value) => {
            const stateFilter = this.$route.query.stateFilter || "active";
            if (stateFilter === "all") return true;
            return value === stateFilter;
          },
          divider: true,
        },
        {
          text: "Abrechnungsgruppe",
          sortKey: 12,
          default: false,
          value: "payrollType.group.title",
          sort: (a, b) => a.title.localeCompare(b.title),
          divider: true,
        },
        {
          text: "Abrechnungsart",
          sortKey: 13,
          default: true,
          value: "payrollType",
          filter: (value) => {
            const payrollTypeFilter =
              this.$route.query.payrollTypeFilter || "all";

            // If "all" is selected, show everything
            if (payrollTypeFilter === "all") {
              return true;
            }

            // If "none" is selected, show nothing
            if (payrollTypeFilter === "none") {
              return false;
            }

            // Handle array of IDs (from URL parsing)
            let selectedIds = [];
            if (Array.isArray(payrollTypeFilter)) {
              selectedIds = payrollTypeFilter;
            } else if (
              typeof payrollTypeFilter === "string" &&
              payrollTypeFilter.includes(",")
            ) {
              selectedIds = payrollTypeFilter.split(",");
            } else if (payrollTypeFilter) {
              selectedIds = [payrollTypeFilter];
            }

            // If no valid selection, show nothing
            if (selectedIds.length === 0) {
              return false;
            }

            // Check if item has payrollType and if it's in the filter
            console.log(value.id, value); // Bug: returns (undefined, 'Title of a payrollType')
            if (!value || !value.id) {
              return false; // Exclude items with no payrollType if specific types are selected
            }
            return selectedIds.includes(value.id);
          },
          sort: (a, b) => a.title.localeCompare(b.title),
          divider: true,
        },
        {
          text: "Anzahl in H",
          sortKey: 20,
          default: false,
          value: "totalSalary.baseVariables.HOURS",
          divider: true,
        },
        {
          text: "Aufwand in EUR",
          sortKey: 21,
          default: false,
          value: "totalSalary.amount",
          divider: true,
        },
        {
          sortKey: 100,
          value: "actions",
          default: true,
          sortable: false,
        },
      ],
      headers: [],
    };
  },
  computed: {
    payrollTypes() {
      return this.$store.state.accounting.payrollTypes;
    },
    payrollTypesList() {
      if (!this.payrollTypes || !this.payrollTypes.length) return [];

      // Group all types together by their "group.id"
      const groupedItems = this.payrollTypes.reduce((acc, item) => {
        if (!acc[item.group.id]) acc[item.group.id] = [];
        acc[item.group.id].push(item);
        return acc;
      }, {});

      // Sort each group by their "sortKey"
      const sortedGroups = Object.values(groupedItems).map((group) =>
        group.sort((a, b) => a.sortKey - b.sortKey),
      );

      // Insert a header with the group.title in front of every group and a divider after every group
      return sortedGroups.flatMap((group) => [
        { header: group[0].group.title },
        ...group,
        { divider: true },
      ]);
    },
    showAllTimeSheets() {
      return this.$store.getters["organization/checkPermissionByID"](
        `${ACCOUNTING_TIME_SHEETS_read_all}`,
      );
    },
    timeSheetsInput() {
      const output = this.timeSheets.map((timeSheet) => {
        return {
          ...timeSheet,
          groupBy: this.groupByDateText(
            timeSheet.billableDuration.start.timestamp.toDate(),
          ),
        };
      });
      return output;
    },
    filteredTimeSheets() {
      const personFilter = this.$route.query.personFilter || "own";

      return this.timeSheetsInput.filter((timeSheet) => {
        // Filter nach Person
        if (
          personFilter === "own" &&
          timeSheet.user.uid !== this.currentUserUid
        ) {
          return false;
        }
        return true;
      });
    },
    computedHeaders() {
      return this.headers; // Use existing headers logic from initializeHeaders
    },
    payrollTypeSelectValue() {
      // Convert the filter value to the format expected by v-select
      const filterValue = this.$route.query.payrollTypeFilter;

      if (filterValue === "all") {
        // Return all payroll type IDs when "all" is selected
        return this.payrollTypesList
          .filter((type) => !type.header && !type.divider)
          .map((type) => type.meta.id);
      } else if (filterValue === "none") {
        // Explicitly empty selection
        return [];
      } else if (Array.isArray(filterValue)) {
        return filterValue;
      } else if (typeof filterValue === "string" && filterValue.includes(",")) {
        return filterValue.split(",");
      } else if (filterValue) {
        return [filterValue];
      } else {
        // No filter set - return empty array (show nothing selected)
        return [];
      }
    },
  },
  watch: {
    filteredTimeSheets() {
      // Reset state when filtered data changes
      this.$nextTick(() => {
        this.resetGroupState();
      });
    },
  },
  mounted() {
    // Initialize state after component is mounted
    this.$nextTick(() => {
      this.resetGroupState();
    });
  },
  updated() {
    // Reset state when component updates (pagination, filtering, etc.)
    this.$nextTick(() => {
      this.resetGroupState();
    });
  },
  created() {
    this.getPayrollTypes();
    this.initializeHeaders();
  },
  methods: {
    expandAllGroups() {
      // Simple approach: click all closed groups
      Object.keys(this.$refs).forEach((key) => {
        const ref = this.$refs[key];
        if (ref && ref.$attrs && !ref.$attrs["data-open"]) {
          ref.$el.click();
        }
      });
      // Update states
      this.allGroupsExpanded = true;
      this.hasClosedGroups = false;
      this.hasOpenGroups = true;
    },

    collapseAllGroups() {
      // Simple approach: click all open groups
      Object.keys(this.$refs).forEach((key) => {
        const ref = this.$refs[key];
        if (ref && ref.$attrs && ref.$attrs["data-open"]) {
          ref.$el.click();
        }
      });
      // Update states
      this.allGroupsExpanded = false;
      this.hasClosedGroups = true;
      this.hasOpenGroups = false;
    },

    onGroupToggle() {
      // Called when individual groups are toggled
      // Check current state and update flags - ONLY for visible groups
      this.$nextTick(() => {
        const refs = Object.keys(this.$refs);
        if (refs.length === 0) {
          // No visible groups - disable both buttons
          this.hasOpenGroups = false;
          this.hasClosedGroups = false;
          this.allGroupsExpanded = true;
          return;
        }

        const openGroups = [];
        const closedGroups = [];

        // Only check refs that actually exist (visible groups)
        refs.forEach((key) => {
          const ref = this.$refs[key];
          if (ref && ref.$attrs) {
            if (ref.$attrs["data-open"]) {
              openGroups.push(key);
            } else {
              closedGroups.push(key);
            }
          }
        });

        // Update state flags based ONLY on visible groups
        this.hasOpenGroups = openGroups.length > 0;
        this.hasClosedGroups = closedGroups.length > 0;
        this.allGroupsExpanded =
          closedGroups.length === 0 && openGroups.length > 0;
      });
    },

    updateGroupState() {
      // Simplified - just trigger state check
      this.onGroupToggle();
    },

    resetGroupState() {
      // Reset to default state - useful when pagination/filters change
      this.$nextTick(() => {
        // Check the current visible groups state
        this.onGroupToggle();
      });
    },

    routeDetails(item) {
      if (!this.showActionBtn) {
        this.openTimeSheet(item);
      }
    },
    handlePayrollTypeChange(selectedIds, updateFilter) {
      const allPayrollTypeIds = this.payrollTypesList
        .filter((type) => !type.header && !type.divider)
        .map((type) => type.meta.id);

      // Check if all items are selected
      if (selectedIds.length === allPayrollTypeIds.length) {
        updateFilter("payrollTypeFilter", "all");
      } else if (selectedIds.length === 0) {
        updateFilter("payrollTypeFilter", []);
      } else {
        updateFilter("payrollTypeFilter", selectedIds);
      }
    },
    getPayrollTypes() {
      return this.$store
        .dispatch(`${ACCOUNTING}${GET_PAYROLL_TYPES}`, {
          organizationId: this.$route.params.organizationId,
        })
        .then(() => {
          // Only initialize with "all" if no filter parameter exists at all
          // This preserves user's empty selections
          if (this.$route.query.payrollTypeFilter === undefined) {
            this.$router.replace({
              query: { ...this.$route.query, payrollTypeFilter: "all" },
            });
          }
        });
    },
    initializeHeaders() {
      // if (this.timeSheetsPersonFilter === "all") {
      // Add all "default" = true items from allColumnHeaders
      this.headers = this.allColumnHeaders.filter((header) => header.default);
      // } else {
      //   // Add all "default" = true items from allColumnHeaders but ignore the first two items
      //   this.headers = this.allColumnHeaders.filter(
      //     (header, index) => header.default && index > 1
      //   );
      // }
    },
    selectAllPayrollTypes(filters, updateFilter) {
      this.$nextTick(() => {
        const allPayrollTypeIds = this.payrollTypesList
          .filter((type) => !type.header && !type.divider)
          .map((type) => type.meta.id);

        const currentFilter = filters.payrollTypeFilter;

        // Check if all are selected
        let allSelected = false;
        if (currentFilter === "all") {
          allSelected = true;
        } else if (Array.isArray(currentFilter)) {
          allSelected = currentFilter.length === allPayrollTypeIds.length;
        } else if (
          typeof currentFilter === "string" &&
          currentFilter.includes(",")
        ) {
          const filterArray = currentFilter.split(",");
          allSelected = filterArray.length === allPayrollTypeIds.length;
        }

        if (allSelected) {
          // Deselect all - set to empty array
          updateFilter("payrollTypeFilter", []);
        } else {
          // Select all - set to "all"
          updateFilter("payrollTypeFilter", "all");
        }
      });
    },
    getPayrollTypeSelectorIcon(payrollTypeFilter) {
      if (!this.payrollTypesList.length) return "mdi-checkbox-blank-outline";

      const allPayrollTypeIds = this.payrollTypesList
        .filter((type) => !type.header && !type.divider)
        .map((type) => type.meta.id);

      // Check if all are selected
      if (payrollTypeFilter === "all") {
        return "mdi-checkbox-marked";
      }

      // Check if none are selected
      if (payrollTypeFilter === "none") {
        return "mdi-checkbox-blank-outline";
      }

      let selectedCount = 0;
      if (Array.isArray(payrollTypeFilter)) {
        selectedCount = payrollTypeFilter.length;
      } else if (
        typeof payrollTypeFilter === "string" &&
        payrollTypeFilter.includes(",")
      ) {
        selectedCount = payrollTypeFilter.split(",").length;
      } else if (
        payrollTypeFilter &&
        payrollTypeFilter !== "all" &&
        payrollTypeFilter !== "none"
      ) {
        selectedCount = 1;
      }

      if (selectedCount === allPayrollTypeIds.length) {
        return "mdi-checkbox-marked";
      }
      if (selectedCount > 0) {
        return "mdi-minus-box";
      }
      return "mdi-checkbox-blank-outline";
    },
    getHoursSum(items) {
      return items.reduce((acc, item) => {
        return acc + item.totalSalary.baseVariables.HOURS;
      }, 0);
    },
    getCompensationSum(items) {
      return items.reduce((acc, item) => {
        return acc + item.totalSalary.amount;
      }, 0);
    },
    openTimeSheet(item, type = "default") {
      if (type === "newTab") {
        window.open(
          this.$router.resolve({
            name: "accounting-time-sheets-details",
            params: {
              organizationId: this.$route.params.organizationId,
              itemId: item.meta.id,
            },
          }).href,
          "_blank",
        );
        return;
      } else {
        this.$router.push({
          name: "accounting-time-sheets-details",
          params: {
            organizationId: this.$route.params.organizationId,
            itemId: item.meta.id,
          },
        });
      }
    },
    groupByDateText(date) {
      const month = ("0" + (date.getMonth() + 1)).slice(-2);
      const year = date.getFullYear();
      const monthLong = date.toLocaleString("de-DE", { month: "long" });
      return year + "-" + month + "_" + monthLong + " " + year;
    },
    stripPrefix(item) {
      return item.substring(item.indexOf("_") + 1);
    },
    getCountOfTimeSheetsInMonth(monthId) {
      return this.filteredTimeSheets.filter(
        (report) => report.groupBy === monthId,
      ).length;
    },
  },
};
</script>
